import { useDraggable, useDroppable } from "@dnd-kit/core";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Link2,
  Lock,
  MoreVertical,
  StickyNote
} from "lucide-react";
import {
  memo,
  useEffect,
  useRef,
  useState,
  type FC,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode
} from "react";

import { FileTypeIcon } from "@/components/common/FileTypeIcon";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { StatusBadge } from "@/components/ui/status-badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { nodeFilename, nodeTitle } from "../../caseFiles/nodeNames";
import { isPrivileged, type FsNode } from "../../caseFiles/types";
import { FileCardButton } from "./FileCardButton";
import type { DropPlacement, TreeRow } from "./treeRows";
import { needReviewLabel } from "./unclassified";

const INDENT = 14;

/** "From Work Products · Employer Support Letter (draft)" for a copied file. */
export const copyTooltip = (
  node: FsNode,
  sourceTitle: string | null | undefined
): string | null => {
  const ref = node.source_ref;
  if (!ref) return null;
  const lines = [
    `From Work Products · ${sourceTitle ?? "a work product"} (${ref.kind})`
  ];
  if (node.source_changed)
    lines.push(`The ${ref.kind} changed since this copy was made`);
  return lines.join("\n");
};

const ROLE_SOURCES: Record<string, string> = {
  attorney: "by an attorney",
  header: "from its header",
  classifier: "by the organizer",
  taxonomy: "by its category",
  agent: "by the case agent"
};

/** Why the file is internal, and what that means for the packet. */
export const privilegedTooltip = (node: FsNode): string => {
  const by = ROLE_SOURCES[node.document_role_source ?? ""];
  const lines = [
    `Internal${by ? `, marked ${by}` : ""}`,
    "Never in the packet; agents read it for facts but can't cite or quote it"
  ];
  if (node.document_role_reason) lines.push(node.document_role_reason);
  return lines.join("\n");
};

/** The name a file was uploaded as, when it differs from what it's called now. */
export const originalNameOf = (node: FsNode): string | null => {
  if (node.kind !== "file") return null;
  const original = (node.original_name ?? "").trim();
  return original && original !== nodeFilename(node) ? original : null;
};

const rowTooltip = (
  node: FsNode,
  title: string,
  sourceTitle: string | null | undefined,
  showingOriginal: boolean
): string => {
  const lines = [node.path_cache];
  const copied = copyTooltip(node, sourceTitle);
  if (copied) lines.push(copied);
  if (showingOriginal) {
    lines.push(`Exhibit title: ${title}`);
  } else if (node.original_name && node.original_name !== nodeFilename(node)) {
    lines.push(`Uploaded as ${node.original_name}`);
  }
  if (node.title_status === "upload_name") {
    lines.push(`"${title}" is still the upload name`);
  }
  if (node.review_note) {
    lines.push(`Note (not printed): ${node.review_note}`);
  }
  return lines.join("\n");
};

export type RowProps = {
  row: TreeRow<FsNode>;
  label: string | null;
  /** The draft or form a copied file came from, for its tooltip. */
  sourceTitle?: string | null;
  selected: boolean;
  /** In the multi-selection, independent of the open-file highlight. */
  picked?: boolean;
  focused: boolean;
  renaming: boolean;
  /** Where a drag currently hovers over this row, if any. */
  dropHint: DropPlacement | null;
  dragging: boolean;
  /** List the file by the name it was uploaded as (Show Original Names). */
  showOriginal?: boolean;
  /** On the Unclassified folder: how many files in it wait for review. */
  reviewCount?: number;
  onToggle: (id: string) => void;
  onClick: (row: TreeRow<FsNode>, e: MouseEvent) => void;
  onDoubleClick: (row: TreeRow<FsNode>) => void;
  onContextMenu: (row: TreeRow<FsNode>, e: MouseEvent) => void;
  /** Checkbox toggle. Shift-click is a range; the row itself does not open. */
  onSelectToggle?: (row: TreeRow<FsNode>, e: MouseEvent) => void;
  /** The three-dot menu is open for this row. */
  actionsOpen?: boolean;
  onActionsOpenChange?: (row: TreeRow<FsNode>, open: boolean) => void;
  /** Menu body, mounted while `actionsOpen`. Same items as right-click. */
  actionsMenu?: ReactNode;
  onRenameCommit: (id: string, name: string) => void;
  onRenameCancel: () => void;
  onKeyDown: (row: TreeRow<FsNode>, e: KeyboardEvent) => void;
};

const RenameInput: FC<{
  initial: string;
  onCommit: (name: string) => void;
  onCancel: () => void;
}> = ({ initial, onCommit, onCancel }) => {
  const [value, setValue] = useState(initial);
  const ref = useRef<HTMLInputElement>(null);
  // The menu that opened this field moves focus in the same turn. Ignore that
  // blur, then take focus once it has settled.
  const armed = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.select();
    armed.current = true;
  }, [initial]);
  const finish = (commit: boolean) => {
    if (commit && value.trim() && value.trim() !== initial)
      onCommit(value.trim());
    else onCancel();
  };
  return (
    <input
      ref={ref}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onBlur={() => {
        if (!armed.current) return;
        finish(true);
      }}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Enter") {
          e.preventDefault();
          armed.current = false;
          finish(true);
        } else if (e.key === "Escape") {
          e.preventDefault();
          armed.current = false;
          onCancel();
        }
      }}
      className="h-5 min-w-0 flex-1 rounded-xs border border-primary bg-elevated px-1 text-base outline-none"
      aria-label="Rename"
    />
  );
};

export const FileRow = memo(function FileRow({
  row,
  label,
  sourceTitle,
  selected,
  picked = false,
  focused,
  renaming,
  dropHint,
  dragging,
  showOriginal = false,
  reviewCount,
  onToggle,
  onClick,
  onDoubleClick,
  onContextMenu,
  onSelectToggle,
  actionsOpen = false,
  onActionsOpenChange,
  actionsMenu = null,
  onRenameCommit,
  onRenameCancel,
  onKeyDown
}: RowProps) {
  const { node, depth, hasChildren, expanded } = row;
  const isFolder = node.kind === "folder";
  const {
    setNodeRef: setDragRef,
    listeners,
    attributes
  } = useDraggable({
    id: `files:${node.id}`,
    data: { tree: "files", nodeId: node.id, row },
    disabled: renaming
  });
  const { setNodeRef: setDropRef } = useDroppable({
    id: `files:${node.id}`,
    data: { tree: "files", row }
  });
  const privileged = isPrivileged(node);
  const excluded = node.packet_inclusion === "excluded" || privileged;
  const heading =
    isFolder && !excluded && node.exhibit_numbering === "unnumbered";
  const title = nodeTitle(node);
  const original = showOriginal ? originalNameOf(node) : null;
  const revealed = selected || picked || focused || actionsOpen;

  return (
    <div
      ref={(el) => {
        setDragRef(el);
        setDropRef(el);
      }}
      {...attributes}
      {...listeners}
      role="treeitem"
      aria-label={
        privileged
          ? `${title} (internal, left out of the packet)`
          : excluded
          ? `${title} (left out of the packet)`
          : isFolder && reviewCount
          ? `${title} (${needReviewLabel(reviewCount).toLowerCase()})`
          : title
      }
      aria-level={depth + 1}
      aria-expanded={isFolder ? expanded : undefined}
      aria-selected={selected}
      data-node-id={node.id}
      tabIndex={focused ? 0 : -1}
      onClick={(e) => onClick(row, e)}
      onDoubleClick={() => onDoubleClick(row)}
      onContextMenu={(e) => onContextMenu(row, e)}
      onKeyDown={(e) => onKeyDown(row, e)}
      className={cn(
        "group relative flex h-(--row-h) w-full items-center gap-1.5 pr-2 text-base text-fg-2 outline-none select-none hover:pr-8",
        revealed && "pr-8",
        selected ? "bg-fill-2 text-fg" : "hover:bg-fill-1",
        focused && "ring-1 ring-inset ring-primary/40",
        dragging && "opacity-40",
        dropHint?.kind === "inside" &&
          "bg-accent-soft ring-1 ring-inset ring-primary"
      )}
      style={{ paddingLeft: 6 + depth * INDENT }}
    >
      {dropHint?.kind === "before" ? (
        <div className="pointer-events-none absolute inset-x-1 top-0 h-0.5 bg-primary" />
      ) : null}
      {dropHint?.kind === "after" ? (
        <div className="pointer-events-none absolute inset-x-1 bottom-0 h-0.5 bg-primary" />
      ) : null}

      <button
        type="button"
        tabIndex={-1}
        aria-label={expanded ? "Collapse" : "Expand"}
        onClick={(e) => {
          e.stopPropagation();
          if (isFolder) onToggle(node.id);
        }}
        onPointerDown={(e) => e.stopPropagation()}
        className={cn(
          "-mr-0.5 flex size-4 shrink-0 items-center justify-center rounded-xs text-fg-4 hover:bg-fill-2 hover:text-fg-2",
          (!isFolder || !hasChildren) && "invisible"
        )}
      >
        {expanded ? (
          <ChevronDown className="size-3.5" />
        ) : (
          <ChevronRight className="size-3.5" />
        )}
      </button>

      <span className="relative flex size-4 shrink-0 items-center justify-center">
        <FileTypeIcon
          name={nodeFilename(node)}
          mimeType={node.mime_type ?? undefined}
          isFolder={isFolder}
          size={14}
          className={cn(
            excluded && "opacity-50",
            !renaming && "group-hover:opacity-0",
            !renaming && revealed && "opacity-0"
          )}
        />
        {!renaming ? (
          <Checkbox
            checked={picked}
            tabIndex={-1}
            aria-label={`Select ${title}`}
            data-testid="file-row-select"
            data-revealed={revealed ? "true" : "false"}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onSelectToggle?.(row, e);
            }}
            className={cn(
              "absolute after:hidden",
              "pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100",
              "focus-visible:pointer-events-auto focus-visible:opacity-100",
              revealed && "pointer-events-auto opacity-100"
            )}
          />
        ) : null}
      </span>

      {renaming ? (
        <RenameInput
          initial={title}
          onCommit={(name) => onRenameCommit(node.id, name)}
          onCancel={onRenameCancel}
        />
      ) : (
        <span
          className={cn(
            "min-w-0 flex-1 truncate",
            excluded && "text-fg-4 line-through decoration-fg-4",
            heading && "text-xs font-medium tracking-wide text-fg-3 uppercase"
          )}
          title={rowTooltip(node, title, sourceTitle, Boolean(original))}
          data-title-status={node.title_status}
          data-original={original ? "true" : undefined}
        >
          {original ?? title}
          {original ? <span className="ml-1.5 text-fg-4">{title}</span> : null}
        </span>
      )}

      {!renaming && node.source_ref ? (
        <span
          className={cn(
            "ml-1 shrink-0",
            node.source_changed ? "text-warn" : "text-fg-3"
          )}
          title={copyTooltip(node, sourceTitle) ?? undefined}
          data-testid="file-row-copy"
          data-changed={node.source_changed ? "true" : undefined}
        >
          {node.source_changed ? (
            <AlertTriangle className="size-3" aria-label="Source changed" />
          ) : (
            <Link2 className="size-3" aria-label="Copy of a work product" />
          )}
        </span>
      ) : null}

      {!renaming && node.review_note ? (
        <span
          className="ml-1 shrink-0 text-warn"
          title={`Note (not printed): ${node.review_note}`}
          aria-label={`Note: ${node.review_note}`}
          data-testid="file-row-review-note"
        >
          <StickyNote className="size-3" aria-hidden />
        </span>
      ) : null}

      {!renaming && label ? (
        <span
          className="ml-1 shrink-0 font-mono text-sm text-fg-4"
          title="Exhibit label"
        >
          {label}
        </span>
      ) : null}
      {!renaming && isFolder && reviewCount ? (
        <StatusBadge
          tone="warn"
          className="ml-1"
          title="Organize couldn't categorize these files. Move each into its section, or run Organize again."
          data-testid="file-row-needs-review"
        >
          <AlertTriangle aria-hidden />
          {needReviewLabel(reviewCount)}
        </StatusBadge>
      ) : null}
      {!renaming && privileged ? (
        <StatusBadge
          tone="purple"
          className="ml-1"
          title={privilegedTooltip(node)}
          data-testid="file-row-privileged"
        >
          <Lock aria-hidden />
          Internal
        </StatusBadge>
      ) : !renaming && excluded ? (
        <span className="ml-1 shrink-0 text-sm text-fg-4">Excluded</span>
      ) : null}

      {!renaming && !isFolder && !privileged ? (
        <FileCardButton node={node} revealed={revealed} />
      ) : null}

      {!renaming ? (
        <DropdownMenu
          open={actionsOpen}
          onOpenChange={(open) => onActionsOpenChange?.(row, open)}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  tabIndex={-1}
                  aria-label={`Actions for ${title}`}
                  data-testid="file-row-actions"
                  data-revealed={revealed ? "true" : "false"}
                  onPointerDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  onDoubleClick={(e) => e.stopPropagation()}
                  className={cn(
                    "absolute top-1/2 right-1 z-10 -translate-y-1/2",
                    "pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100",
                    "focus-visible:pointer-events-auto focus-visible:opacity-100",
                    revealed && "pointer-events-auto opacity-100",
                    revealed ? "bg-fill-2" : "group-hover:bg-fill-1"
                  )}
                >
                  <MoreVertical className="size-3.5" />
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="top">More Actions</TooltipContent>
          </Tooltip>
          {actionsMenu}
        </DropdownMenu>
      ) : null}
    </div>
  );
});
